import React, { useMemo } from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import ConfirmModal from "../modal";

const normalizeSessionDateValue = (value) => {
	if (!value) return null;

	if (typeof value === "string") {
		return value
			.replace("T", " ")
			.replace(/\.\d{3}Z$/, "")
			.replace(/Z$/, "");
	}

	return moment.utc(value).format("YYYY-MM-DD HH:mm:ss");
};

const parseLaosSessionDate = (value) => {
	const normalized = normalizeSessionDateValue(value);
	if (!normalized) return null;

	const parsed = moment.utc(normalized, "YYYY-MM-DD HH:mm:ss", true);
	return parsed.isValid() ? parsed : null;
};

const formatSessionDate = (value) => {
	const parsed = parseLaosSessionDate(value);
	return parsed ? parsed.format("DD/MM/YYYY HH:mm:ss") : "-";
};

const getSessionState = (user) => {
	if (user?.isOnline !== null && user?.isOnline !== undefined) {
		return {
			isOnline: Number(user.isOnline) === 1,
			lastOnlineMinutes: user?.lastOnlineMinutes ?? null,
		};
	}

	const tokenStart = parseLaosSessionDate(user?.tokenStart);
	const tokenEnd = parseLaosSessionDate(user?.tokenEnd);
	const nowLaos = moment.utc().add(7, "hours");

	if (!tokenStart || !tokenEnd) {
		return { isOnline: false, lastOnlineMinutes: null };
	}

	const isOnline = nowLaos.isBetween(tokenStart, tokenEnd, null, "[]");
	const lastOnlineMinutes = isOnline
		? 0
		: Math.max(0, nowLaos.diff(tokenEnd, "minutes"));

	return { isOnline, lastOnlineMinutes };
};

const UserProfileModal = ({ show, onHide, user, loading = false }) => {
	const { t } = useTranslation();

	const displayName = useMemo(() => {
		return `${user?.firstName || ""} ${user?.lastName || ""}`.trim() || "-";
	}, [user]);

	const initials = useMemo(() => {
		const parts = displayName.split(" ").filter(Boolean);
		return parts
			.slice(0, 2)
			.map((part) => part.charAt(0).toUpperCase())
			.join("") || "U";
	}, [displayName]);

	const { isOnline, lastOnlineMinutes } = getSessionState(user);

	const lastOnlineText = isOnline
		? t("callcenter.customerService.onlineNow")
		: lastOnlineMinutes === null || lastOnlineMinutes === undefined
			? "-"
			: t("callcenter.customerService.minutesAgo", { count: lastOnlineMinutes });

	const accountStatus =
		Number(user?.status) === 1
			? t("status.active")
			: Number(user?.status) === 2
				? t("status.lock")
				: t("status.inActive");

	return (
		<ConfirmModal
			show={show}
			onHide={onHide}
			title={t("callcenter.customerService.userInformation")}
			size="lg"
			centered
			hideConfirm={true}
			cancelText={t("close")}
			dialogClassName="gwin-profile-modal-dialog"
		>
			{loading && !user?.userId ? (
				<div className="gwin-profile-loading">
					<span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
					<span>{t("uiText.text017")}</span>
				</div>
			) : (
				<div className="gwin-profile-modal">
					<div className="gwin-profile-hero">
						<div className="gwin-profile-avatar">{initials}</div>
						<div className="gwin-profile-heading">
							<div className="gwin-profile-name-row">
								<h5>{displayName}</h5>
								<span className={`gwin-profile-online ${isOnline ? "online" : "offline"}`}>
									<span className="gwin-profile-online-dot" />
									{isOnline
										? t("callcenter.customerService.online")
										: t("callcenter.customerService.offline")}
								</span>
							</div>
							<div className="gwin-profile-meta">
								<span><i className="fas fa-id-card" /> ID: {user?.userId || "-"}</span>
								<span><i className="fas fa-phone" /> {user?.phone || user?.username || "-"}</span>
							</div>
						</div>
					</div>

					<div className="gwin-profile-section">
						<div className="gwin-profile-section-title">
							<i className="fas fa-user" />
							<span>{t("callcenter.customerService.userInformation")}</span>
						</div>

						<div className="gwin-profile-grid">
							<div className="gwin-profile-field">
								<small>{t("callcenter.customerService.username")}</small>
								<strong>{user?.username || user?.phone || "-"}</strong>
							</div>
							<div className="gwin-profile-field">
								<small>{t("callcenter.customerService.accountStatus")}</small>
								<strong>{accountStatus}</strong>
							</div>
							<div className="gwin-profile-field">
								<small>{t("callcenter.customerService.userType")}</small>
								<strong>{user?.rolesCode || user?.roleCode || user?.userTypeId || "-"}</strong>
							</div>
							<div className="gwin-profile-field">
								<small>{t("callcenter.customerService.agentCode")}</small>
								<strong>{user?.agentCode || user?.agentId || "-"}</strong>
							</div>
							<div className="gwin-profile-field">
								<small>{t("callcenter.customerService.provinceName")}</small>
								<strong>{user?.provinceName || user?.province || "-"}</strong>
							</div>
							<div className="gwin-profile-field">
								<small>{t("callcenter.customerService.districtName")}</small>
								<strong>{user?.districtName || user?.district || "-"}</strong>
							</div>
							<div className="gwin-profile-field gwin-profile-field-wide">
								<small>{t("callcenter.customerService.village")}</small>
								<strong>{user?.village || "-"}</strong>
							</div>
						</div>
					</div>

					<div className="gwin-profile-section gwin-profile-session-section">
						<div className="gwin-profile-section-title">
							<i className="fas fa-clock" />
							<span>{t("callcenter.customerService.sessionInformation")}</span>
						</div>

						<div className="gwin-profile-session-grid">
							<div className="gwin-profile-session-card">
								<span className="gwin-profile-session-icon start"><i className="fas fa-play" /></span>
								<div>
									<small>{t("callcenter.customerService.tokenStart")}</small>
									<strong>{formatSessionDate(user?.tokenStart)}</strong>
								</div>
							</div>
							<div className="gwin-profile-session-card">
								<span className="gwin-profile-session-icon end"><i className="fas fa-stop" /></span>
								<div>
									<small>{t("callcenter.customerService.tokenEnd")}</small>
									<strong>{formatSessionDate(user?.tokenEnd)}</strong>
								</div>
							</div>
							<div className="gwin-profile-session-card">
								<span className="gwin-profile-session-icon time"><i className="fas fa-hourglass-half" /></span>
								<div>
									<small>{t("callcenter.customerService.tokenTime")}</small>
									<strong>{user?.tokenTime ? `${user.tokenTime} ${t("callcenter.customerService.minutes")}` : "-"}</strong>
								</div>
							</div>
							<div className="gwin-profile-session-card">
								<span className="gwin-profile-session-icon login"><i className="fas fa-sign-in-alt" /></span>
								<div>
									<small>{t("callcenter.customerService.lastLogin")}</small>
									<strong>{formatSessionDate(user?.lastLogin)}</strong>
								</div>
							</div>
						</div>

						<div className="gwin-profile-last-online">
							<span>{t("callcenter.customerService.lastOnline")}</span>
							<strong>{lastOnlineText}</strong>
						</div>
					</div>
				</div>
			)}
		</ConfirmModal>
	);
};

export default UserProfileModal;
